import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FunctionComponent,
} from "react";

import { AssetDDPanel } from "./AssetDDPanel";
import { AssetInput } from "./AssetInput";
import { TokenPill, WEI, Dollars } from "@components";

import "./SelectableAssetDeposit.scss";
import type { ChainId, NumericString } from "@types";
import { set } from "react-ga";
import { bi, n, sn } from "@helpers";

import type { TokenDefinition } from "@bracket/addressbook";

export declare type SelectableAssetDepositProps = {
  chain: ChainId;
  token: TokenDefinition;
  tokens?: TokenDefinition[];
  amount: NumericString;

  busy?: boolean;

  interactive?: boolean;

  interactiveInput?: boolean;

  /** show all tokens available for selection */
  allTokens?: boolean;

  /** show only swappable tokens available for selection */
  swappableTokens?: boolean;

  /** force input for a specific token ONLY */
  fixedToken?: boolean;

  exclude?: string[];

  /** show all tokens available for selection */
  showBalance?: boolean;
  showCalcluatedDollars?: boolean;
  balance?: bigint;
  balanceUSD?: bigint;
  calculatedValue?: bigint;
  showMax?: boolean;

  amountValid?: boolean;
  amountInvalidReason?: string;
  // this is a hack because state isnt being honoured, so this variable will be used on the global scope until
  // we provide a better solution.
  hackvar: string;

  onTokenChange?: (token: TokenDefinition) => void;
  onValueChange?: (value: NumericString) => void;
  onChange?: (token: TokenDefinition, value: NumericString) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  applyMax?: (token?: TokenDefinition) => void;
};

export const SelectableAssetDeposit: FunctionComponent<
  SelectableAssetDepositProps
> = ({
  chain,
  token,
  amount,
  balance,
  balanceUSD,
  calculatedValue,
  swappableTokens,
  exclude,
  allTokens,
  fixedToken,
  tokens = [],
  amountValid = true,
  amountInvalidReason = "",
  busy = false,
  interactive = true,
  interactiveInput = true,
  showBalance = false,
  showCalcluatedDollars = false,
  showMax = true,
  onTokenChange = () => {},
  onValueChange = () => {},
  onChange = () => {},
  applyMax = () => {},
  onBlur = () => {},
  onFocus = () => {},
  hackvar,
}) => {
  const classes = () =>
    ["selectable-asset-deposit", busy ? "busy" : null]
      .filter((v) => v)
      .join(" ");

  const [rect, setRect] = useState<DOMRect>(null!);
  const [validAmount, setValidAmount] = useState(true);
  const [, setForceRender] = useState<any>();
  const overSelector = useRef(false);
  const choosing = useRef(false);

  // not being honoured for some reason see property hackvar for more info.
  // const [ mouseOutsidePanel, setMouseOutsidePanel ] = useState(true);

  const selRef = useRef<HTMLDivElement>(null);
  const ddRef = useRef<HTMLDivElement>(null);

  const reRender = () => {
    setForceRender(Object.entries(choosing.current));
  };

  const onClientResize = () => {
    if (selRef.current) {
      setRect(selRef.current.getBoundingClientRect());
      return;
    }
  };

  useEffect(() => {
    const { visualViewport } = window;
    if (visualViewport) {
      visualViewport.addEventListener("resize", onClientResize);
      window.addEventListener("scroll", onClientResize);
      window.addEventListener("mouseup", onMouseUp);
      return () => {
        visualViewport.removeEventListener("resize", onClientResize);
        window.removeEventListener("scroll", onClientResize);
        window.removeEventListener("mouseup", onMouseUp);
      };
    }

    window.addEventListener("resize", onClientResize);
    window.addEventListener("scroll", onClientResize);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("resize", onClientResize);
      window.removeEventListener("scroll", onClientResize);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (selRef.current) {
      setRect(selRef.current.getBoundingClientRect());
    }
  }, [choosing.current]);

  const onMouseUp = () => {
    if (!overSelector.current) {
      choosing.current = false;
      reRender();
    }
  };

  const onActivate = () => {
    if (interactive) {
      choosing.current = !choosing.current;
      reRender();
    }
  };

  const onSelect = (token: TokenDefinition) => {
    if (token.inactive) return;
    choosing.current = false;
    onTokenChange(token);
    onChange(token, amount);
    reRender();
  };

  const onAmountChange = (amount: NumericString) => {
    onValueChange(amount);
    onChange(token, amount);
    if (balance !== undefined) {
      setValidAmount(bi(sn(amount)) <= balance);
    }
  };

  const onFocusInput = () => {
    choosing.current = false;
    reRender();
    onFocus();
  };

  const onApplyMax = () => applyMax();

  const onMouseOverSelector = () => {
    if (overSelector.current === false) {
      overSelector.current = true;
    }
  };

  const onMouseOoutSelector = () => {
    if (overSelector.current === true) {
      overSelector.current = false;
    }
  };

  const selectorStyle = (): CSSProperties => {
    if (tokens.length > 5) {
      return { minWidth: "140px" };
    }
    return {};
  };

  return (
    <div
      className={classes()}
      onMouseOver={onMouseOverSelector}
      onMouseOut={onMouseOoutSelector}
    >
      <div className="panel">
        <div className="selector" onClick={onActivate} ref={selRef}>
          <TokenPill
            token={token}
            chevron={!fixedToken}
            chevronDirection={choosing.current ? "down" : "up"}
            container
            style={selectorStyle()}
          />
        </div>
        <AssetInput
          amount={amount}
          neverNegative
          interactive={interactive && interactiveInput}
          busy={busy}
          onChange={onAmountChange}
          onBlur={onBlur}
          onFocus={onFocusInput}
        />
        {showMax ? (
          <div className="max" onClick={onApplyMax}>
            Max
          </div>
        ) : null}
      </div>
      {showBalance ? (
        <div className="footer">
          <div className="balance">
            Balance:
            <WEI value={balance} /> {token.symbol}
          </div>
          <div className="balance_usd">
            <Dollars value={balanceUSD} />
            USD
          </div>
        </div>
      ) : null}
      {showCalcluatedDollars ? (
        <div className="footer">
          <Dollars value={calculatedValue} /> USD
        </div>
      ) : null}
      {choosing.current && rect && !fixedToken && !busy && (
        <AssetDDPanel
          active={token}
          tokens={tokens}
          rect={rect}
          onSelect={onSelect}
          exclude={exclude || undefined}
          ref={ddRef}
        />
      )}
    </div>
  );
};
